import { IApiAxiosError } from "@/api";
import { createCarrier } from "@/api/carriers/carriers";
import { ICarrierCreate } from "@/api/carriers/carriers.types";
import { Button } from "@/components/shared/buttons/Button";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";

export const CreateCarrier = () => {
    const [modalOpen, setModalOpen] = React.useState(false);
    const [carrierName, setCarrierName] = React.useState("");
    const queryClient = useQueryClient();

    const createMutation = useMutation({
        mutationFn: (data: ICarrierCreate) => createCarrier(data),
        onSuccess: () => {
            setModalOpen(false);
            setCarrierName("");
            queryClient.invalidateQueries({ queryKey: ["admin-carriers"] });
        },
        onError: (error) => {
            console.error("Error creating carrier:", error);
        },
    });

    const handleCreate = () => {
        if (!carrierName.trim()) return;

        createMutation.mutate({ carrier_name: carrierName });
    };

    const handleClose = () => {
        setModalOpen(false);
        setCarrierName("");
        createMutation.reset();
    };

    const axiosError = createMutation.error as IApiAxiosError;

    return (
        <>
            <Button
                variant="contained"
                color="secondary"
                sx={{ borderRadius: 29 }}
                disableElevation
                onClick={() => setModalOpen(true)}
            >
                Add New Carrier
            </Button>

            {/* Create Carrier Modal */}
            <Modal
                open={modalOpen}
                onClose={handleClose}
                title="Create a New Carrier"
                actions={[
                    {
                        label: "Create",
                        onClick: handleCreate,
                        variant: "contained",
                        color: "primary",
                        disabled:
                            !carrierName.trim() || createMutation.isPending,
                    },
                ]}
                footerText={
                    axiosError && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{
                                fontSize: "0.75rem",
                            }}
                        >
                            {axiosError.response?.data.detail ||
                                "An error occurred while creating the carrier."}
                        </Typography>
                    )
                }
            >
                <TextField
                    label="Carrier Name"
                    error={!!axiosError}
                    value={carrierName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCarrierName(e.target.value)
                    }
                    onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                            handleCreate();
                        }
                    }}
                    inputProps={{ autoFocus: true }}
                    sx={{ width: "100%" }}
                />
            </Modal>
        </>
    );
};
